import { useEffect, useState } from "react";
import { Card } from "antd";
import { useParams } from "react-router-dom";
import { NegotiationStatus } from "../../../types/NegotiationTypes";
import { OrderForm, OrderFormDynamicLever } from "../../../types/OrderFormTypes";
import { Vendor } from "../../../types/VendorTypes";
import { Supplier } from "../../../types/SupplierTypes";
import {
    getOrderForm,
    getOrderFormDynamicLevers,
    listOrders,
    updateOrderForm,
} from "../../../common/api/OrderFormClient";
import { getAnalysisScores } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { getVendors } from "../../../common/api/VendorClient";
import { getSupplierByName } from "../../../common/api/SupplierClient";
import { EmployeeCount } from "../../../types/FinanceTypes";
import { getEmployeeCountsBySymbol } from "../../../common/api/FinanceClient";
import { VersionScore } from "./components/DealScoreBar";
import { Insights } from "./components/Insights";
import { LerversTable } from "./components/LerversTable";

export const OrderFormScoreView = () => {
    const params = useParams();
    const orderFormId = Number(params.orderFormId);
    const [loading, setLoading] = useState(false);
    const [orderForm, setOrderForm] = useState<OrderForm>();
    const [vendor, setVendor] = useState<Vendor>();
    const [supplier, setSupplier] = useState<Supplier>();
    const [orderCount, setOrderCount] = useState(0);
    const [employees, setEmployees] = useState<EmployeeCount[]>();
    const [levers, setLevers] = useState<OrderFormDynamicLever[]>();
    const [versionScores, setVersionScores] = useState<VersionScore[]>();

    const refreshAll = async () => {
        setLoading(true);
        try {
            const _orderForm = await getOrderForm(orderFormId);
            setOrderForm(_orderForm.orderForm);

            refreshLevers();

            // retrieve the past scores to display on the bar
            if (_orderForm.orderForm.projectId) {
                const rawScores = await getAnalysisScores(_orderForm.orderForm.projectId);
                const scores: VersionScore[] = rawScores.map((row) => ({
                    version: row.version,
                    label: `V${row.version}`,
                    score: row.orderScore,
                }));
                setVersionScores(scores);
            }

            if (_orderForm.orderForm.vendorKey) {
                const [vendors, count] = await Promise.all([
                    getVendors([_orderForm.orderForm.vendorKey]),
                    listOrders({ pageSize: 10, current: 1 }, [], [], undefined, [
                        _orderForm.orderForm.vendorKey,
                    ]),
                ]);
                setVendor(vendors[0]);
                setOrderCount(count.pagination.total);

                if (vendors[0].name) {
                    const _supplier = await getSupplierByName(vendors[0].name);
                    setSupplier(_supplier);

                    if (_supplier.symbol) {
                        const _employees = await getEmployeeCountsBySymbol(_supplier.symbol);
                        setEmployees(_employees);
                    }
                }
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const refreshLevers = async () => {
        try {
            const res = await getOrderFormDynamicLevers(orderFormId);
            setLevers(res);
        } catch (error: any) {
            handleError(error);
        }
    };

    const handleStatusChange = async (status: NegotiationStatus) => {
        if (!orderForm) return;

        try {
            const updatedOrderForm = {
                ...orderForm,
                negotiationStatus: status,
            };
            await updateOrderForm(updatedOrderForm);
            setOrderForm(updatedOrderForm);
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        refreshAll();
    }, [orderFormId]);

    const content = (showNav = true) => (
        <>
            <Insights
                name={orderForm?.name}
                savings={orderForm?.decrease}
                orderFormId={orderFormId}
                vendorProps={{
                    ...supplier,
                    name: supplier?.name || vendor?.name,
                    logo: supplier?.logo || vendor?.hero,
                    employees,
                    contracts: orderCount,
                    vendorKey: vendor?.vendorKey,
                }}
                scoreBarProps={{
                    score: orderForm?.orderScore,
                    perfectScore: orderForm?.optimizedScore,
                    versionScores,
                }}
                toolboxProps={{
                    status: orderForm?.negotiationStatus,
                    onStatusChange: handleStatusChange,
                }}
                style={{ marginTop: 16 }}
            />
            <LerversTable levers={levers} style={{ marginTop: 16 }} />
        </>
    );

    return (
        <Card bordered={false} loading={loading}>
            {content()}
        </Card>
    );
};
